/**
 * SortTableData
 * @param {Array<object>} data        – array to sort
 * @param {"ASC"|"DESC"|"DEF"} order  – "DEF" → no sorting, default "ASC"
 * @param {string} key                – key present on every object
 * @returns {Array<object>}           – new array in requested order
 *
 * ASC precedence: ␠ → specials → 0‑9 → A‑Z → a‑z
 * DESC = reverse; DEF = original order.
 */
export function SortTableData(data, order = "ASC", key) {
  if (!Array.isArray(data)) throw new TypeError("First argument must be an array");
  if (!["ASC", "DESC", "DEF"].includes(order))
    throw new TypeError(`order must be "ASC", "DESC" or "DEF"`);
  if (typeof key !== "string") throw new TypeError("key must be a string");

  // ---------- DEF → short‑circuit ------------------------------------------
  if (order === "DEF") return [...data];   // shallow copy, no sorting

  // ---------- category lookup ----------------------------------------------
  const getCategory = (ch) => {
    const code = ch.charCodeAt(0);
    if (code === 32) return 0;                              // space
    if (
      (code >= 33 && code <= 47) ||                         // !"#$%&'()*+,-./
      (code >= 58 && code <= 64) ||                         // :;<=>?@
      (code >= 91 && code <= 96) ||                         // [\]^_`
      (code >= 123 && code <= 126)                          // {|}~
    ) return 1;                                             // specials
    if (code >= 48 && code <= 57) return 2;                 // digits
    if (code >= 65 && code <= 90) return 3;                 // uppercase
    if (code >= 97 && code <= 122) return 4;                // lowercase
    return 5;                                               // other (Unicode)
  };

  const cmpChar = (a, b) => {
    const ca = getCategory(a);
    const cb = getCategory(b);
    if (ca !== cb) return ca - cb;
    return a.localeCompare(b);
  };

  const cmpString = (sa, sb) => {
    const len = Math.min(sa.length, sb.length);
    for (let i = 0; i < len; i++) {
      const diff = cmpChar(sa[i], sb[i]);
      if (diff !== 0) return diff;
    }
    return sa.length - sb.length;
  };

  const compareValues = (va, vb) => {
    if (va == null && vb == null) return 0;
    if (va == null) return -1;
    if (vb == null) return 1;
    return cmpString(String(va), String(vb));
  };

  const direction = order === "ASC" ? 1 : -1;
  return [...data].sort((a, b) => direction * compareValues(a[key], b[key]));
}